import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

const baseClasses =
  'h-10 w-full appearance-none rounded-md border bg-[var(--field-surface)] ps-3 pe-9 text-base ' +
  'text-[var(--text-primary)] transition-colors duration-fast ease-standard ' +
  'focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-[var(--field-focus-ring)] ' +
  'disabled:cursor-not-allowed disabled:bg-[var(--field-surface-disabled)] disabled:opacity-60';

/**
 * Native <select> wrapped with a custom chevron so it matches the Input field
 * styling (the browser default arrow is hidden via `appearance-none`). The
 * forwarded ref points at the underlying <select>.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid = false, children, disabled, ...rest },
  ref,
) {
  return (
    <span className="relative block w-full">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        className={cn(
          baseClasses,
          invalid
            ? 'border-[var(--danger-border)]'
            : 'border-[var(--border-subtle)] hover:border-[var(--border-strong)]',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-y-0 end-3 flex items-center text-[var(--text-muted)]',
          disabled && 'opacity-60',
        )}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M3 4.5 6 7.5 9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </span>
  );
});
